import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import GoogleMap from '../../components/GoogleMap';

const LeipzigHome = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-turquoise-50 to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden" data-testid="leipzig-hero">
        <div className="absolute inset-0 bg-gradient-to-r from-turquoise-400 to-turquoise-300 opacity-10"></div>
        <div className="container mx-auto text-center relative z-10">
          <div className="mb-8">
            <img 
              src="https://beratung.grossstadtherzen.de/logo-transparent.png" 
              alt="Großstadtherzen Logo" 
              className="h-80 w-auto mx-auto mb-6"
            />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Ambulante Pflege Großstadtherzen®
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8">
            Standort <span className="text-turquoise-400 font-bold">Leipzig</span>
          </p>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Bei uns werden sie „Groß" geschrieben
          </p>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-8">
            Herzlich Willkommen
          </h2>
          <div className="prose prose-lg mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Wir sind ein kompetentes Team aus qualifizierten Pflegekräften und bieten Ihnen ein umfangreiches Paket an pflegerischer Unterstützung an, um Ihnen die bestmögliche individuelle Pflege und Beratung zu versichern.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Ob Versorgung nach Pflegegraden, Klienten im Rahmen der Hauswirtschaft oder Betreuung sowie Klienten die einer Behandlungspflege bedürfen – wir helfen Ihnen gern.
            </p>
          </div>
        </div>
      </section>

      {/* Special Announcement - Wound Certification */}
      <section className="py-12 px-4 bg-gradient-to-r from-green-50 to-turquoise-50">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-xl p-8 border-l-4 border-green-500">
            <div className="flex items-start space-x-4">
              <div className="bg-green-500 text-white p-3 rounded-full">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">NEU!!!</h3>
                <p className="text-lg text-gray-700">
                  Wir sind ab sofort ein <strong>zertifizierter Pflegedienst für chronische und schwer heilende Wunden!</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pflegeberatung Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-gradient-to-br from-turquoise-400 to-turquoise-400 rounded-3xl shadow-2xl p-8 md:p-12 text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Pflegeberatung nach § 37 Abs. 3 SGB XI</h2>
            <p className="text-xl mb-8">
              AB SOFORT bieten wir unseren Klienten in Sachsen die Pflegeberatung bequem telefonisch an.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="font-bold text-lg mb-2">⏰ Flexibel & unkompliziert</h3>
                <p className="text-turquoise-50">Wählen Sie Ihren Wunschtermin – ganz nach Ihrem Zeitplan.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="font-bold text-lg mb-2">€ 100% kostenfrei</h3>
                <p className="text-turquoise-50">Für Sie entstehen keinerlei Kosten.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="font-bold text-lg mb-2">☎ Einfach & bequem</h3>
                <p className="text-turquoise-50">Beratung direkt am Telefon – ohne Anfahrt, ohne Aufwand.</p>
              </div>
            </div>
            <a 
              href="https://exciting-germain-1.emergent.host/" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-turquoise-400 px-8 py-4 rounded-full text-lg font-semibold hover:bg-turquoise-50 transition-all shadow-lg"
              data-testid="pflegeberatung-link"
            >
              Zur Pflegeberatung →
            </a>
          </div>
        </div>
      </section>

      {/* CTA Buttons */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="grid md:grid-cols-2 gap-6">
            <Link 
              to="/leipzig/ueber-uns"
              className="bg-turquoise-400 text-white p-8 rounded-2xl text-center hover:bg-turquoise-500 transition-all shadow-lg hover:shadow-2xl transform hover:-translate-y-1"
              data-testid="ueber-uns-link"
            >
              <h3 className="text-2xl font-bold mb-2">ÜBER UNS</h3>
              <p className="text-turquoise-100">Erfahren Sie mehr über unser Team und unsere Philosophie</p>
            </Link>
            <Link 
              to="/angebot"
              className="bg-turquoise-400 text-white p-8 rounded-2xl text-center hover:bg-turquoise-500 transition-all shadow-lg hover:shadow-2xl transform hover:-translate-y-1"
              data-testid="angebot-link"
            >
              <h3 className="text-2xl font-bold mb-2">UNVERBINDLICHES ANGEBOT</h3>
              <p className="text-turquoise-100">Fordern Sie jetzt ein kostenloses Angebot an</p>
            </Link>
          </div>
        </div>
      </section>

      {/* New Office Announcement */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl shadow-xl p-8 border-l-4 border-purple-500">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">NEU!!! Beratungsbüro in Gohlis</h3>
            <p className="text-lg text-gray-700">
              Ab sofort haben wir für unsere Klienten in Gohlis ein Beratungsbüro in der <strong>Lindenthaler Str. 43</strong> (direkt an der Bushaltestelle Georg-Schumann/ Lindenthaler Str.)
            </p>
          </div>
        </div>
      </section>

      {/* Quality Message */}
      <section className="py-16 px-4 bg-gradient-to-br from-turquoise-400 to-turquoise-400 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Auch Ihnen steht eine qualitativ hochwertige Pflege zu.
          </h2>
          <p className="text-xl mb-4">Nutzen Sie diese.</p>
          <p className="text-2xl font-bold">
            Qualitativ hochwertige Pflege darf nicht am Geld scheitern.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Kontakt Leipzig</h2>
          <div className="bg-gradient-to-br from-turquoise-50 to-turquoise-100 rounded-3xl shadow-xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2 text-turquoise-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Adresse
                </h3>
                <p className="text-gray-700">
                  Ambulante Pflege Großstadtherzen® UG<br />
                  Zschochersche Str. 49<br />
                  04229 Leipzig
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2 text-turquoise-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Kontakt
                </h3>
                <p className="text-gray-700">
                  <a href="tel:034124927833" className="hover:text-turquoise-400 transition-colors font-semibold">Tel: 0341-24927833</a><br />
                  <span className="text-gray-600">Fax: 0341-24927832</span><br />
                  <a href="mailto:info@grossstadtherzen.de" className="hover:text-turquoise-400 transition-colors">info@grossstadtherzen.de</a>
                </p>
              </div>
            </div>
            <GoogleMap location="leipzig" className="mt-6" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LeipzigHome;